import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spacer,
  Text,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import WizardStepper from "./WizardStepper";
import WizardSummary from "./WizardSummary";
import useWizardState from "./useWizardState";
import Step0SmartStart from "./steps/Step0SmartStart";
import Step1Channel from "./steps/Step1Channel";
import Step2UseCase from "./steps/Step2UseCase";
import Step3BusinessDetails, {
  getMissingRequiredFields,
} from "./steps/Step3BusinessDetails";
import Step4Outcomes from "./steps/Step4Outcomes";
import Step5Connect from "./steps/Step5Connect";
import Step6ReviewDeploy from "./steps/Step6ReviewDeploy";
import Step7Test from "./steps/Step7Test";

const STEPS = [
  { title: "Smart start" },
  { title: "Channel" },
  { title: "Use case" },
  { title: "Business details" },
  { title: "Outcomes" },
  { title: "Connect" },
  { title: "Review & deploy" },
  { title: "Test" },
];

export default function BuildAgentWizard() {
  const navigate = useNavigate();
  const cancelDialog = useDisclosure();
  const cancelRef = useRef();
  const wiz = useWizardState();
  const [deployResult, setDeployResult] = useState(null);

  const hasState =
    wiz.draft.config ||
    wiz.draft.channel ||
    Object.keys(wiz.draft.fieldValues || {}).length > 0;

  // Post-deploy the assistant + widgets exist on the server, so the
  // wizard draft is no longer "unsaved work" — it's just stale local
  // state. Closing in that mode should reset cleanly without a
  // confirmation dialog.
  const isDeployed = !!deployResult?.assistantId;

  const handleClose = () => {
    if (isDeployed) {
      wiz.reset();
      setDeployResult(null);
      navigate("/dashboard");
    } else if (hasState) {
      cancelDialog.onOpen();
    } else {
      navigate("/dashboard");
    }
  };

  const handleConfirmCancel = () => {
    wiz.reset();
    setDeployResult(null);
    cancelDialog.onClose();
    navigate("/dashboard");
  };

  const handleBuildAnother = () => {
    wiz.reset();
    setDeployResult(null);
    wiz.setActiveStep(0);
  };

  const onConfigParsed = (config) => {
    wiz.ingestConfig(config);
    const skipAhead =
      (config.confidence ?? 0) >= 0.8 &&
      (!config.missing_critical_fields ||
        config.missing_critical_fields.length === 0);
    wiz.setActiveStep(skipAhead ? 6 : 3);
  };

  const onChannelPicked = (channel) => {
    wiz.update({ channel });
    wiz.setActiveStep(2);
  };

  const onTemplatePicked = (template) => {
    wiz.ingestConfig(template.config);
    wiz.setActiveStep(3);
  };

  const onDeployed = (result) => {
    setDeployResult(result);
    wiz.setActiveStep(7);
  };

  const renderStep = () => {
    switch (wiz.activeStep) {
      case 0:
        return (
          <Step0SmartStart
            onConfigParsed={onConfigParsed}
            onChannelPicked={onChannelPicked}
          />
        );
      case 1:
        return <Step1Channel value={wiz.draft.channel} onPick={onChannelPicked} />;
      case 2:
        return (
          <Step2UseCase
            channel={wiz.draft.channel}
            onTemplatePicked={onTemplatePicked}
          />
        );
      case 3:
        return (
          <Step3BusinessDetails
            config={wiz.draft.config}
            fieldValues={wiz.draft.fieldValues}
            onFieldChange={wiz.setFieldValue}
          />
        );
      case 4:
        return (
          <Step4Outcomes
            config={wiz.draft.config}
            outcomeSelections={wiz.draft.outcomeSelections}
            onToggleOutcome={wiz.toggleOutcome}
            crmChoice={wiz.draft.crmChoice}
            onCrmChoice={(v) => wiz.update({ crmChoice: v })}
            summaryEmail={wiz.draft.summaryEmail}
            onSummaryEmail={(v) => wiz.update({ summaryEmail: v })}
            webhookUrl={wiz.draft.webhookUrl}
            onWebhookUrl={(v) => wiz.update({ webhookUrl: v })}
          />
        );
      case 5:
        return (
          <Step5Connect
            config={wiz.draft.config}
            outcomeSelections={wiz.draft.outcomeSelections}
            crmChoice={wiz.draft.crmChoice}
            toolBindings={wiz.draft.toolBindings || {}}
            onToolBinding={wiz.setToolBinding}
            knowledgeFiles={wiz.draft.knowledgeFiles || []}
            onFileSelected={(files) => wiz.update({ knowledgeFiles: files })}
          />
        );
      case 6:
        return (
          <Step6ReviewDeploy
            draft={wiz.draft}
            onDeployed={onDeployed}
          />
        );
      case 7:
        return (
          <Step7Test
            draft={wiz.draft}
            deployResult={deployResult}
            onBuildAnother={handleBuildAnother}
            onDone={() => {
              wiz.reset();
              setDeployResult(null);
              navigate("/dashboard");
            }}
          />
        );
      default:
        return null;
    }
  };

  // Footer behaviour per step:
  //   Step 0:  no footer at all — it's the entry point.
  //   Step 7:  no footer — it's the celebration screen.
  //   Step 1/2/6:  the step body owns the primary action (channel pick,
  //                template pick, deploy). Show Back only.
  //   Step 3/4/5:  show Back + Next.
  const showFooter = wiz.activeStep > 0 && wiz.activeStep < 7;
  const showNext = ![1, 2, 6].includes(wiz.activeStep);

  // Step 3 blocks Next when any required field is unfilled and not
  // explicitly skipped. Other steps don't gate.
  const nextDisabled =
    wiz.activeStep === 3 &&
    getMissingRequiredFields(wiz.draft.config, wiz.draft.fieldValues).length > 0;

  return (
    <Box minH="100vh" bgGradient="linear(to-br, blue.50, purple.50)">
      <Flex
        align="center"
        px={6}
        py={4}
        bg="white"
        borderBottomWidth="1px"
        position="sticky"
        top={0}
        zIndex={2}
      >
        <Text fontWeight="bold" fontSize="lg">
          Build agent
        </Text>
        <Spacer />
        <Box flex="1" maxW="720px" mx={8} data-tour="wizard.stepper">
          <WizardStepper steps={STEPS} activeStep={wiz.activeStep} />
        </Box>
        <Spacer />
        <IconButton
          aria-label="Close wizard"
          icon={<IoClose />}
          variant="ghost"
          onClick={handleClose}
        />
      </Flex>

      <Box
        px={6}
        py={10}
        maxW="960px"
        mx="auto"
        data-tour={`wizard.step${wiz.activeStep}`}
      >
        {wiz.activeStep > 0 && wiz.activeStep < 7 && (
          <Box mb={6}>
            <WizardSummary draft={wiz.draft} />
          </Box>
        )}
        {renderStep()}

        {showFooter && (
          <HStack mt={8} justify="space-between">
            <Button variant="ghost" onClick={wiz.goBack}>
              ← Back
            </Button>
            {showNext && (
              <Button
                colorScheme="blue"
                onClick={wiz.goNext}
                isDisabled={nextDisabled}
              >
                Next →
              </Button>
            )}
          </HStack>
        )}
      </Box>

      <AlertDialog
        isOpen={cancelDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={cancelDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Discard your progress?</AlertDialogHeader>
            <AlertDialogBody>
              Nothing has been saved on the server yet. Closing now will
              discard your wizard draft.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={cancelDialog.onClose}>
                Keep editing
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleConfirmCancel}>
                Discard
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
